import { Box, TableCell, TableRow } from "@mui/material";
import React from "react";
import { DotLegalAlert, DotLegalEmptyState } from "@dotlegal/dotlegal-ui-components";
import { ITableHeader } from "@dotlegal/dotlegal-ui-components";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import { useTranslation } from "../../../localization/useTranslation";
import { BaseSharingRowsModel, BaseSharingRowsTableModel, SharingBaseTableViewModel } from "./SharingsTable.types";
import NoSharings from "../../processingActivityOverview/processingActivitySharingsTab/noSharings.svg?react";
import { useUrlProvider } from "../../../useUrlProvider";
import { StepTypeEnum } from "../../ProcessingActivity.types";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useSharingsBaseTableStyles } from "./SharingsTable.styles";
import { useSharingsBaseTableHook } from "./SharingsTable.hooks";
import { isNullOrWhitespace } from "../../../common/stringOperations";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { informationIcon } from "../../../common/icons";
import DotLegalLink from "../../../common/components/dotLegalLink/DotLegalLink";
import { SharingOrigin } from "../../processingActivityOverview/ProcessingActivityOverview.types";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";

export interface ISharingsBaseTableProps {
    data: SharingBaseTableViewModel | undefined;
    isLoading: boolean;
    emptyStateText: string;
    sessionStorageKey: string;
}

function SharingsBaseTable(props: ISharingsBaseTableProps) {
    const { translateString } = useTranslation();
    const { getEditProcessingActivityUrl, getProcessingActivityOverviewUrl } = useUrlProvider();
    const { sharingsDataRows, hasSharings, getFilterOptions, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange, hasActiveFilters } =
        useSharingsBaseTableHook(props);

    const styles = useSharingsBaseTableStyles();
    const { permissions } = useUserContext();

    function getProcessingActivityLink(row: BaseSharingRowsModel) {
        if (permissions?.processingActivityPermissions.read) {
            return (
                <DotLegalLink linkColor={"primary"} to={getProcessingActivityOverviewUrl(row.id)}>
                    {row.name}
                </DotLegalLink>
            );
        }

        return row.name;
    }

    function getSharingLink(row: BaseSharingRowsModel, role: string) {
        if (hasSharingAccess(row.sharingOrigin)) {
            return (
                <DotLegalLink linkColor={"primary"} to={getEditProcessingActivityUrl(row.id, StepTypeEnum.sharings, row.subStep, row.sharingId)}>
                    {role}
                </DotLegalLink>
            );
        }

        return role;
    }

    function hasSharingAccess(sharingOrigin: SharingOrigin) {
        if (sharingOrigin.dataControllerId) {
            return permissions?.processingActivityDataControllersPermissions?.permissions?.read;
        } else if (sharingOrigin.sourceId) {
            return permissions?.processingActivitySourcesPermissions?.permissions?.read;
        } else if (sharingOrigin.transferId) {
            return permissions?.processingActivityDisclosuresPermissions?.permissions?.read;
        } else if (sharingOrigin.dataProcessorId) {
            return permissions?.processingActivityDataProcessorsPermissions?.permissions?.read;
        } else {
            return permissions?.processingActivityJointDataControllerPermissions?.permissions?.read;
        }
    }

    const limitReachedAlert = props.data?.processingActivityLimitReached ? (
        <DotLegalAlert
            severity="info"
            marginBottom={2}
            warningText={translateString("sharingsProcessingActivityLimitReached", { count: props.data.totalProcessingActivityCount })}
        />
    ) : null;

    return hasSharings ? (
        <React.Fragment>
            {limitReachedAlert}
            <DotLegalTableWithFilterBar
                filterBar={{
                    isLoading: props.isLoading,
                    filterData: getFilterOptions(),
                    fetchFilterData: true,
                    onMenuClose: onFilterChange,
                    onRemove: onFilterChange,
                    pinnedFilterIds: pinnedFilterIds,
                    onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                }}
                headers={GetSharingsHeader(translateString)}
                rowsPerPage={30}
                defaultOrderBy={"senderName"}
                clickableRows={false}
                defaultOrder={"asc"}
                isLoading={props.isLoading}
                data={sharingsDataRows}
                emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noSharingsMatchesQuery")}
                renderRow={(row, i) => (
                    <TableRow key={i}>
                        <TableCell>
                            <div>
                                <Box sx={styles.legalEntity}>{row.senderName}</Box>
                                {row.senderId !== null && <Box sx={styles.entityRole}>{getSharingLink(row, row.senderRole)}</Box>}
                            </div>
                        </TableCell>
                        <TableCell>
                            <div style={{ display: "flex", flexWrap: "wrap" }}>
                                {row.dataCategories.map((x, i) => {
                                    return (
                                        <div key={x.id} style={{ marginRight: 8, display: "flex", alignItems: "center" }}>
                                            <div style={{ display: "inline-block", marginRight: 3 }}>{x.name}</div>
                                            {!isNullOrWhitespace(x.description) && (
                                                <DotLegalTooltip text={x.description}>
                                                    <Box sx={styles.tooltipIcon}>{informationIcon}</Box>
                                                </DotLegalTooltip>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </TableCell>
                        <TableCell>
                            <div>
                                <Box sx={styles.legalEntity}>
                                    {row.recipientName}
                                    {row.recipientInThirdCountry && (
                                        <DotLegalTooltip text={translateString("recipientFromInsecureThirdCountry")}>
                                            {<ReportProblemOutlinedIcon fontSize={"small"} />}
                                        </DotLegalTooltip>
                                    )}
                                </Box>
                                {row.recipientId !== null && <Box sx={styles.entityRole}>{getSharingLink(row, row.recipientRole)}</Box>}
                            </div>
                        </TableCell>
                        <TableCell>
                            <Box sx={styles.entityRole}>{getProcessingActivityLink(row)}</Box>
                        </TableCell>
                    </TableRow>
                )}
            />
        </React.Fragment>
    ) : (
        <React.Fragment>
            {limitReachedAlert}
            <DotLegalEmptyState icon={<NoSharings />} text={props.emptyStateText} />
        </React.Fragment>
    );
}

export default SharingsBaseTable;

function GetSharingsHeader(translateString: any) {
    const headers = Array<ITableHeader<BaseSharingRowsTableModel>>();
    headers.push({ property: "senderName", text: translateString("Sender"), align: "left", showOnMobile: true });
    headers.push({ property: "dataCategories", text: translateString("dataCategoryHeader"), align: "left", showOnMobile: true });
    headers.push({ property: "recipientName", text: translateString("recipient"), align: "left", showOnMobile: true });
    headers.push({ property: "name", text: translateString("processingActivity"), align: "left", showOnMobile: true });
    return headers;
}
